"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Send, Trash2 } from "lucide-react";

export interface VoiceRecorderProps {
  onSend: (blob: Blob) => void;
  onCancel: () => void;
}

export function VoiceRecorder({ onSend, onCancel }: VoiceRecorderProps) {
  const [duration, setDuration] = useState(0);
  const [waveform, setWaveform] = useState<number[]>(Array(24).fill(3));
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number>(null); // requestAnimationFrame ID for smooth timer
  const startTimeRef = useRef<number>(0);
  const animFrameRef = useRef<number>(null);
  const mountedRef = useRef(true);

  const audioCtxRef = useRef<AudioContext | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) { cancelAnimationFrame(timerRef.current); timerRef.current = null; }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    mediaRecorderRef.current = null;
  }, []);

  const handleSend = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      if (blob.size > 0) onSend(blob);
      else onCancel();
    };

    if (recorder.state === 'recording') {
      recorder.stop();
    }
    cleanup();
  }, [onSend, onCancel, cleanup]);

  const handleCancel = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === 'recording') {
      recorder.onstop = () => {}; // Don't send on stop
      recorder.stop();
    }
    cleanup();
    onCancel();
  }, [onCancel, cleanup]);

  useEffect(() => {
    mountedRef.current = true;
    // Guard against StrictMode double-mount creating duplicate streams
    if (streamRef.current) return;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        if (!mountedRef.current || streamRef.current) {
          // Already mounted or another stream exists — kill this one
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        streamRef.current = stream;
        const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.start(200);

        // Smooth timer using requestAnimationFrame + start timestamp
        startTimeRef.current = Date.now();
        const tickTimer = () => {
          if (!mountedRef.current) return;
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setDuration(elapsed);
          timerRef.current = requestAnimationFrame(tickTimer);
        };
        timerRef.current = requestAnimationFrame(tickTimer);

        // Waveform from analyser — only src → analyser, NOT to destination (prevents echo)
        const ctx = new AudioContext();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 64;
        src.connect(analyser);
        // DO NOT connect analyser to ctx.destination — that would play mic back through speakers

        const updateWave = () => {
          if (!mountedRef.current) return;
          const data = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(data);
          setWaveform(Array.from({ length: 24 }, (_, i) => {
            const idx = Math.floor((i / 24) * data.length);
            return Math.max(2, (data[idx] / 255) * 28);
          }));
          animFrameRef.current = requestAnimationFrame(updateWave);
        };
        updateWave();
      } catch {
        onCancel();
      }
    })();

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, []);

  const fmt = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="flex items-center gap-3 bg-zinc-100 dark:bg-zinc-800/50 rounded-xl px-4 py-3 w-full">
      {/* Cancel */}
      <button onClick={handleCancel} className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors" title="Discard">
        <Trash2 className="w-4 h-4 text-red-500" />
      </button>

      {/* Waveform */}
      <div className="flex items-center gap-[3px] flex-1 h-8">
        {waveform.map((h, i) => (
          <div key={i} className="w-[3px] rounded-full bg-blue-500 transition-all duration-100" style={{ height: `${h}px` }} />
        ))}
      </div>

      {/* Timer + recording indicator */}
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-mono text-zinc-600 dark:text-zinc-300 min-w-[40px]">
          {fmt(duration)}
        </span>
      </div>

      {/* Send */}
      <button onClick={handleSend} className="p-2.5 rounded-full bg-blue-600 hover:bg-blue-700 transition-colors" title="Send voice message">
        <Send className="w-4 h-4 text-white" />
      </button>
    </div>
  );
}
